import { adjectives, animals, colors, uniqueNamesGenerator } from "unique-names-generator";

import { LayoutLocator, SortablePayload } from "./types";

export const getInitials = (name: string) => {
  // eslint-disable-next-line unicorn/better-regex
  const regex = new RegExp(/(\p{L}{1})\p{L}+/, "gu");
  const initials = [...name.matchAll(regex)];

  return ((initials.shift()?.[1] ?? "") + (initials.pop()?.[1] ?? "")).toUpperCase();
};

export const isUrl = (string: string | null | undefined) => {
  if (!string) return false;

  const urlRegex = /https?:\/\/[^\n ]+/i;
  const dataUrlRegex = /^data:image\/[a-zA-Z+]+;base64,/;

  return urlRegex.test(string) || dataUrlRegex.test(string);
};

export const isEmptyString = (string: string) => {
  if (string === "<p></p>") return true;
  if (string === '<p class="mb-1"><br></p>') return true;
  return string.trim().length === 0;
};

export const extractUrl = (string: string) => {
  const urlRegex = /https?:\/\/[^\n ]+/i;

  const result = string.match(urlRegex);
  return result ? result[0] : null;
};

export const kebabCase = (string?: string | null) => {
  if (!string) return "";

  return (
    string
      .match(/[A-Z]{2,}(?=[A-Z][a-z]+\d*|\b)|[A-Z]?[a-z]+\d*|[A-Z]|\d+/g)
      ?.join("-")
      .toLowerCase() ?? ""
  );
};

const resumeAdjectives = [
  "Professional",
  "Strategic",
  "Results-Driven",
  "High-Impact",
  "Modern",
  "Polished",
  "Executive",
  "Career-Ready",
  "Growth-Focused",
  "Client-Ready",
  "People-First",
  "Innovative",
  "Leadership",
  "Agile",
  "Data-Driven",
  "Creative",
  "Operational",
  "Customer-Centric",
  "Revenue-Focused",
  "High-Performing",
];

const professionalRoles = [
  "Product Manager",
  "Software Engineer",
  "Data Analyst",
  "UX Designer",
  "Project Manager",
  "Marketing Manager",
  "Sales Leader",
  "Operations Manager",
  "Customer Success Manager",
  "Business Analyst",
  "Financial Analyst",
  "DevOps Engineer",
  "Product Designer",
  "Content Strategist",
  "QA Engineer",
  "Data Scientist",
  "Copywriter",
  "Solutions Architect",
  "Digital Marketer",
  "Account Manager",
  "HR Specialist",
  "Support Lead",
  "Creative Director",
  "Supply Chain Manager",
  "Cybersecurity Analyst",
];

const pickRandom = <T>(items: T[]): T => items[Math.floor(Math.random() * items.length)];

export type RandomNameKind = "resume" | "cover" | "generic";

export const generateRandomName = (kind: RandomNameKind = "resume") => {
  if (kind === "resume") {
    const adjective = pickRandom(resumeAdjectives);
    const role = pickRandom(professionalRoles);
    return `${adjective} ${role} Resume`;
  }

  if (kind === "cover") {
    const adjective = pickRandom(resumeAdjectives);
    const role = pickRandom(professionalRoles);
    return `${adjective} ${role} Cover Letter`;
  }

  // Fallback generic combination
  return uniqueNamesGenerator({
    dictionaries: [adjectives, colors, animals],
    style: "capital",
    separator: " ",
  });
};

export const processUsername = (string?: string | null) => {
  if (!string) return "";

  return string.replace(/[^\d.A-Za-z-]/g, "").toLowerCase();
};

export const parseLayoutLocator = (payload: SortablePayload | null): LayoutLocator => {
  if (!payload) return { page: 0, column: 0, section: 0 };

  const section = payload.index;
  const [page, column] = payload.containerId.split(".").map(Number);

  return { page, column, section };
};
